import React from 'react';
import { useQuery } from '@apollo/client';
import { CandidateAttachmentsData, JobAttachmentsData } from '../../../interfaces';
import { GET_CANDIDATE_ATTACHMENTS, GET_JOB_ATTACHMENTS } from '../../../GraphQL/attachments/queries';
import AttachmentList from '../AttachmentList';
import AttachmentUpload from '../AttachmentUpload';

interface AttachmentSectionProps {
  entityId: number;
  type: 'candidate' | 'job';
  onUploadComplete?: () => void;
  onAttachmentDeleted?: () => void;
}

const AttachmentSection: React.FC<AttachmentSectionProps> = ({
  entityId,
  type,
  onUploadComplete,
  onAttachmentDeleted
}) => {
  const query = type === 'candidate' ? GET_CANDIDATE_ATTACHMENTS : GET_JOB_ATTACHMENTS;
  const variables = type === 'candidate' ? { candidateId: entityId } : { jobId: entityId };

  const { data, loading, refetch } = useQuery<
    CandidateAttachmentsData | JobAttachmentsData
  >(query, {
    variables,
    fetchPolicy: 'cache-and-network'
  });

  const attachments = type === 'candidate'
    ? (data as CandidateAttachmentsData)?.candidateAttachments || []
    : (data as JobAttachmentsData)?.jobAttachments || [];

  const hasAttachments = !loading && attachments.length > 0;

  const handleUploadComplete = () => {
    refetch(); // Refresh the attachments list
    onUploadComplete?.(); // Call the original callback if provided
  };

  const handleAttachmentDeleted = () => {
    refetch(); // Refresh the attachments list
    onAttachmentDeleted?.(); // Call the original callback if provided
  };

  if (!hasAttachments) {
    // Show only the upload button when no attachments
    return (
      <AttachmentUpload
        entityId={entityId}
        type={type}
        onUploadComplete={handleUploadComplete}
      />
    );
  }

  // Show full section with header and list when attachments exist
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Attachments</h6>
        <AttachmentUpload
          entityId={entityId}
          type={type}
          onUploadComplete={handleUploadComplete}
        />
      </div>
      <AttachmentList
        entityId={entityId}
        type={type}
        onAttachmentDeleted={handleAttachmentDeleted}
      />
    </>
  );
};

export default AttachmentSection;